import { useCallback, useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { FileInput, TextInput } from '../inputs';
import {
  useDispatch,
  addResource,
  uploadFiles,
  deleteResource,
  fetchResourceById,
} from '../../../state';
import { useRefresh, useNotify } from '../../../hooks';
import {
  convertNullFieldsToEmptyString,
  generateDocumentsApi,
} from '../../../lib';
import { DocumentAPI, FileType, SignedUrlMapping } from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => any;
  warehouseId: string | number;
  appointmentId?: number | string;
  documentId?: number | string;
  getFiles?: (newFiles: Array<FileType>) => any;
}

interface Payload {
  appointmentID: number;
  documents: Array<DocumentAPI>;
}

interface State {
  name: string;
  description: string;
}

const initialState: State = {
  name: '',
  description: '',
};

export const AppointmentDocumentUpload = ({
  open,
  handleClose,
  warehouseId,
  appointmentId,
  documentId,
  getFiles,
}: Props) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const notify = useNotify();

  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<Array<FileType>>([]);
  const [state, setState] = useState(initialState);
  // const [configuredDocumentFees, setConfiguredDocumentFees] = useState([]);

  useEffect(() => {
    (async function () {
      if (documentId && appointmentId && warehouseId) {
        const { data } = await dispatch(
          fetchResourceById({
            baseUrl: `warehouses/${warehouseId}/appointments`,
            id: appointmentId,
            path: `documents/${documentId}`,
          }),
        );
        if (data) {
          const { document, id, ...otherData } = data;
          const { name, description, ...rest } = document || {};
          setState((cur) => ({
            ...cur,
            ...rest,
            ...convertNullFieldsToEmptyString({
              name,
              description,
              ...otherData,
            }),
          }));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, documentId, dispatch]);

  const handleSubmit = useCallback(
    async (values) => {
      if (!documentId && !files.length) {
        return notify('Please upload a file', 'error');
      }

      if (typeof getFiles === 'function') {
        getFiles(files);
        handleClose();
        return;
      }
      if (!appointmentId) return;
      setSubmitting(true);
      let googleResponse, signedUrlsMapping;

      if (!documentId) {
        const { data, error: uploadError } = await dispatch(uploadFiles(files));
        if (uploadError) {
          return setSubmitting(false);
        }
        googleResponse = data?.googleResponse;
        signedUrlsMapping = data?.signedUrlsMapping;
      }
      const payload = generatePayload({
        appointmentId,
        files,
        signedUrlsMapping,
        values,
        hasUpload: !documentId,
      });
      if (documentId || googleResponse) {
        const { error } = await dispatch(
          addResource({
            baseUrl: `/warehouses/${warehouseId}/appointments/${appointmentId}/documents`,
            payload,
            message: 'Changes saved',
          }),
        );
        setSubmitting(false);
        if (!error) {
          refresh();
          handleClose();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, files, appointmentId, warehouseId],
  );

  const handleDelete = useCallback(async () => {
    if (!documentId) return;
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: `warehouses/${warehouseId}/appointments/documents`,
        id: documentId,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      refresh();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, warehouseId]);

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Upload document</span>}
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
      isEdit={!!documentId}
      handleDelete={handleDelete}
    >
      {/* <SelectInput
        name='documentType'
        formikProps={formik}
        label='Document type'
        className={classes.input}
        options={documentTypes}
        required
      /> */}
      <TextInput
        name='name'
        formikProps={formik}
        label='Document name'
        className={classes.input}
      />
      {!documentId && (
        <FileInput
          files={files}
          setFiles={setFiles}
          filePrefix={`appointments/${appointmentId ?? 'create'}-`}
          multiple
        />
      )}
    </FormModal>
  );
};

const useStyles = makeStyles({ name: { AppointmentDocumentUpload } })(
  (theme) => ({
    title: {
      fontFamily: 'Montserrat',
      fontSize: 20,
      fontWeight: 600,
    },
    input: {
      marginBottom: 16,
      width: 300,
    },
  }),
);

interface GenerateParams {
  appointmentId: number | string;
  files: Array<FileType>;
  signedUrlsMapping: SignedUrlMapping;
  values: State;
  hasUpload: boolean;
}

function generatePayload({
  appointmentId: appointmentID,
  files,
  signedUrlsMapping,
  values,
  hasUpload,
}: GenerateParams) {
  const { name, description, ...rest } = values;

  // payload.BillableToCustomer = BillableToCustomer;

  const Document = rest as DocumentAPI;

  if (hasUpload) {
    const { key, rawFile: { type } = {} } = files[0];
    Document.objectName = key;
    Document.url = signedUrlsMapping[key];
    Document.type = type ?? '';
  }

  const payload = {
    appointmentID,
    // documentType,
    documents: [],
  } as Payload;

  if (hasUpload) {
    payload.documents = generateDocumentsApi({
      signedUrlsMapping: signedUrlsMapping,
      files,
      name,
      description,
    });
  } else {
    payload.documents = [{ description, name, ...rest }];
  }

  return payload;
}

const schema = object().shape({
  name: string(),
  description: string().nullable(),
});
